import { wsMoment } from "@wingspanhq/utils/dist/date/wsMoment";

export type BankHoliday = {
  /** ISO calendar date in `YYYY-MM-DD` (date-only, no time / timezone). */
  date: string;
  /** Display name, e.g. `"Independence Day (observed)"`. */
  name: string;
};

const DATE_FORMAT = "YYYY-MM-DD";

/** Bank holidays are evaluated in US Eastern time so the banner flips over at ET midnight. */
const TIMEZONE = "America/New_York";

const DEFAULT_BUSINESS_DAYS = 7;

const FALLBACK_HOLIDAY_NAME = "Bank holiday";

/**
 * Names a US bank (Federal Reserve) holiday from its date alone — no per-year
 * table, so it stays correct as the calendar extends.
 *
 * Which days are holidays is owned by `@wingspanhq/utils` (`isHoliday()`); this
 * only supplies the human-readable name, which that package does not store. Each
 * federal holiday occupies a distinct `(month, day-range)` slot, so the month
 * plus day uniquely identify it. Fixed-date holidays gain "(observed)" when they
 * are shifted off their nominal day (weekend → adjacent weekday); the six
 * always-floating Monday/Thursday holidays are never marked observed.
 *
 * `dateString` is `YYYY-MM-DD` and is assumed to be a real bank holiday (callers
 * gate on `isHoliday()`); anything unrecognized falls back to a generic label.
 */
export const getBankHolidayName = (dateString: string): string => {
  const [, monthPart, dayPart] = dateString.split("-");
  const month = Number(monthPart);
  const day = Number(dayPart);

  // Fixed-date holiday: nominal day → plain name, otherwise weekend-observed.
  const fixed = (name: string, nominalDay: number) =>
    day === nominalDay ? name : `${name} (observed)`;

  switch (month) {
    case 1:
      // New Year's Day (Jan 1, or Jan 2 when Jan 1 is a Sunday) vs MLK (3rd Mon).
      return day <= 2
        ? fixed("New Year's Day", 1)
        : "Martin Luther King Jr. Day";
    case 2:
      return "Presidents' Day"; // 3rd Monday
    case 5:
      return "Memorial Day"; // last Monday
    case 6:
      return fixed("Juneteenth", 19);
    case 7:
      return fixed("Independence Day", 4);
    case 9:
      return "Labor Day"; // 1st Monday
    case 10:
      return "Columbus Day"; // 2nd Monday
    case 11:
      // Veterans Day (Nov 11 ±1 observed) vs Thanksgiving (4th Thursday, 22–28).
      return day <= 12 ? fixed("Veterans Day", 11) : "Thanksgiving Day";
    case 12:
      // Christmas (Dec 25 ±1 observed); Dec 31 is New Year's Day observed early.
      return day === 31
        ? "New Year's Day (observed)"
        : fixed("Christmas Day", 25);
    default:
      return FALLBACK_HOLIDAY_NAME;
  }
};

/**
 * Returns the bank holidays falling within `businessDays` business days of
 * `today` (inclusive), sorted ascending. A holiday on `today` is included;
 * holidays already in the past are excluded.
 *
 * Which days are holidays comes entirely from `@wingspanhq/utils` via
 * `isHoliday()` — the same source that drives Wingspan's payroll business-day
 * math — so the trigger can't disagree with the payment calendar.
 *
 * "Today" is resolved in {@link TIMEZONE} so the result is stable regardless of
 * the viewer's local timezone; comparisons then use date-only moments to avoid
 * DST / offset drift.
 */
export const getUpcomingBankHolidays = (options?: {
  businessDays?: number;
  today?: Date;
}): BankHoliday[] => {
  const businessDays = options?.businessDays ?? DEFAULT_BUSINESS_DAYS;

  const todayDateString = wsMoment(options?.today ?? new Date())
    .tz(TIMEZONE)
    .format(DATE_FORMAT);
  const cursor = wsMoment(todayDateString, DATE_FORMAT);
  const windowEnd = cursor.clone().businessAdd(businessDays);

  const holidays: BankHoliday[] = [];
  while (cursor.isSameOrBefore(windowEnd, "day")) {
    if (cursor.isHoliday()) {
      const date = cursor.format(DATE_FORMAT);
      holidays.push({ date, name: getBankHolidayName(date) });
    }
    cursor.add(1, "day");
  }

  return holidays;
};
